@if (error) {
  <div class="error-banner">
    <div class="error-banner__content">{{ error.message }}</div>
  </div>
}

<div class="main_body">
  <div class="dashboard">
    <!-- Page Header -->
    <app-page-header [title]="i18n.title.dashboard" icon="dashboard" />

    <div class="dashboard__content">
    @if (isLoading()) {
      <!-- Skeleton Loading (uses actual card classes for identical sizing) -->
      <div class="dashboard-stat-cards">
        @for (_ of [1,2,3,4]; track $index) {
          <div class="dashboard-stat-card">
            <div class="dashboard-stat-card__icon-box skel" style="background: none;"></div>
            <div class="dashboard-stat-card__content">
              <div class="skel" style="width: 60%; height: 12px;"></div>
              <div class="skel" style="width: 45%; height: 24px;"></div>
              <div class="skel" style="width: 80%; height: 10px;"></div>
            </div>
          </div>
        }
      </div>
      <div class="dashboard-main">
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <div class="skel" style="width: 160px; height: 18px;"></div>
            <div class="skel" style="width: 24px; height: 24px;"></div>
          </div>
          <div class="dashboard-card__body">
            <div class="skel" style="width: 100%; height: 260px; border-radius: var(--radius-md);"></div>
          </div>
        </div>
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <div class="skel" style="width: 120px; height: 18px;"></div>
            <div class="skel" style="width: 24px; height: 24px;"></div>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-notice-list">
              @for (_ of [1,2,3,4]; track $index) {
                <div class="dashboard-notice-item">
                  <div class="skel" style="width: 80px; height: 11px;"></div>
                  <div class="skel" style="width: 90%; height: 14px;"></div>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    } @else {
      <!-- Row 1: 4-column Stat Cards -->
      <div class="dashboard-stat-cards">
        <!-- Staff Count -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--purple">
            <mat-icon>people</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label" i18n="@@adminSection.home.label.staffCount">スタッフ数</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfStaffs | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (staffTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="staffTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="staffTrend < 0">
                <mat-icon>{{ staffTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ staffTrend > 0 ? '+' : '' }}{{ staffTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Contributor Count -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--warning">
            <mat-icon>diversity_3</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label" i18n="@@adminSection.home.label.contributorCount">多職種スタッフ数</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfContributors | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (contributorTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="contributorTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="contributorTrend < 0">
                <mat-icon>{{ contributorTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ contributorTrend > 0 ? '+' : '' }}{{ contributorTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- User Count -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--success">
            <mat-icon>person</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label" i18n="@@adminSection.home.label.userCount">ユーザー数</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ numOfUsers | number }}</span>
              <span class="dashboard-stat-card__unit" i18n="@@common.unit.people">名</span>
            </div>
            @if (userTrend !== 0) {
              <div class="dashboard-stat-card__trend"
                   [class.dashboard-stat-card__trend--positive]="userTrend > 0"
                   [class.dashboard-stat-card__trend--negative]="userTrend < 0">
                <mat-icon>{{ userTrend > 0 ? 'trending_up' : 'trending_down' }}</mat-icon>
                <span>{{ userTrend > 0 ? '+' : '' }}{{ userTrend }} {{ i18n.label.monthOverMonth }}</span>
              </div>
            } @else {
              <div class="dashboard-stat-card__trend dashboard-stat-card__trend--neutral">
                <mat-icon>trending_flat</mat-icon>
                <span>±0 {{ i18n.label.monthOverMonth }}</span>
              </div>
            }
          </div>
        </div>

        <!-- Data Usage -->
        <div class="dashboard-stat-card">
          <div class="dashboard-stat-card__icon-box dashboard-stat-card__icon-box--info">
            <mat-icon>storage</mat-icon>
          </div>
          <div class="dashboard-stat-card__content">
            <span class="dashboard-stat-card__label" i18n="@@adminSection.home.label.currentUsage">今月の使用量</span>
            <div class="dashboard-stat-card__value">
              <span class="dashboard-stat-card__number">{{ currentUsageGB | number:'1.1-1' }}</span>
              <span class="dashboard-stat-card__unit">GB</span>
            </div>
            <div class="dashboard-stat-card__progress">
              <div class="dashboard-stat-card__progress-fill"
                   [class.dashboard-stat-card__progress-fill--warning]="usagePercentage > 80"
                   [class.dashboard-stat-card__progress-fill--danger]="usagePercentage > 95"
                   [style.width.%]="usagePercentage"></div>
            </div>
            <span class="dashboard-stat-card__sub-info"
                  [class.dashboard-stat-card__sub-info--warning]="usagePercentage > 80">
              {{ i18n.label.remaining }} {{ remainingGB | number:'1.1-1' }} GB / {{ quotaLimitGB }} GB
            </span>
          </div>
        </div>
      </div>

      <!-- Row 2: Chart + Notices -->
      <div class="dashboard-main">
        <!-- Chart Card -->
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title">{{ i18n.title.dataUsage }}（{{ currentYear }}年）</h2>
            <mat-icon class="dashboard-card__icon">bar_chart</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-chart-area">
              <apx-chart
                [series]="chartSeries"
                [chart]="chartOptions"
                [xaxis]="chartXAxis"
                [yaxis]="chartYAxis"
                [dataLabels]="chartDataLabels"
                [plotOptions]="chartPlotOptions"
                [stroke]="chartStroke"
                [fill]="chartFill"
                [grid]="chartGrid"
                [tooltip]="chartTooltip"
                [colors]="chartColors"
              ></apx-chart>
            </div>
          </div>
        </div>

        <!-- Notices Card -->
        <div class="dashboard-card">
          <div class="dashboard-card__header">
            <h2 class="dashboard-card__title" i18n="@@adminSection.home.title.importantNotice">重要なお知らせ</h2>
            <mat-icon class="dashboard-card__icon">info</mat-icon>
          </div>
          <div class="dashboard-card__body">
            <div class="dashboard-notice-list">
              @for (notice of notices; track notice.date) {
                <div class="dashboard-notice-item">
                  <span class="dashboard-notice-item__date">{{ notice.date }}</span>
                  <p class="dashboard-notice-item__text">
                    @if (notice.link) {
                      <a [routerLink]="notice.link">{{ notice.text }}</a>
                    } @else {
                      {{ notice.text }}
                    }
                  </p>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    }
    </div>
  </div>
</div>
